import { Box, CircularProgress, Collapse } from '@mui/material';
import { useMemo } from 'react';
import type { GeneratedKey, RegistrationKey } from '../../instanceTypes';
import { GeneratedKeyBanner } from './GeneratedKeyBanner';
import { KeyCard } from './KeyCard';
import { KeysEmptyState } from './KeysEmptyState';

interface RegistrationKeySectionProps {
  keys: RegistrationKey[];
  loading: boolean;
  revoking: boolean;
  generating: boolean;
  onRevoke: (key: RegistrationKey) => void;
  onGenerate: () => void;
  newKey?: GeneratedKey | null;
  onDismissKey?: () => void;
  /** Keys listed without generate/revoke buttons (ADR-040 D5). */
  readOnly?: boolean;
}

export function RegistrationKeySection({
  keys,
  loading,
  revoking,
  generating,
  onRevoke,
  onGenerate,
  newKey,
  onDismissKey,
  readOnly = false,
}: RegistrationKeySectionProps) {
  const lastKey = useMemo(() => {
    if (keys.length === 0) return null;
    return [...keys].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
  }, [keys]);

  return (
    <Box>
      <Collapse in={!!newKey}>
        {newKey && onDismissKey && (
          <GeneratedKeyBanner generatedKey={newKey} onDismiss={onDismissKey} />
        )}
      </Collapse>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : lastKey ? (
        <KeyCard
          lastKey={lastKey}
          revoking={revoking}
          generating={generating}
          readOnly={readOnly}
          onRevoke={onRevoke}
          onGenerate={onGenerate}
        />
      ) : (
        <KeysEmptyState generating={generating} readOnly={readOnly} onGenerate={onGenerate} />
      )}
    </Box>
  );
}
